import React, { ChangeEvent, useMemo, useState } from 'react';
import { Card, Col, Collapse, Row, Table as ReactTable } from 'react-bootstrap';
import PageInfo from './PageInfo';
import { Pagination } from './Pagination';
import './Table.scss';
import { ApiSortDirection } from '../api';
import { getDisplayName, isChildField } from '../schema/helper';
import Sorter from './Sorter';
import LoadingOverlay from '../feedback/LoadingOverlay';
import debounce from 'lodash.debounce';
import ShowHideButton from '../common/ShowHideButton';
import { InternalApiEntitySchema } from '../api-internal';

interface TableAttribute {
  key: string;
  sortable: boolean;
}

interface TableInterface<Item> {
  total: number;
  page: number;
  size: number;
  items: Item[];
  body: (items: Item[]) => React.ReactNode;
  fetchPage: (page: number) => void;
  header?: (items: Item[]) => React.ReactNode;
  footer?: () => React.ReactNode;
  title?: string | React.ReactNode;
  actions?: () => React.ReactNode;
  filters?: () => React.ReactNode;
  query?: string;
  onSearch?: (query: string | undefined) => void;
  attributes?: TableAttribute[] | undefined;
  schema?: InternalApiEntitySchema | undefined;
  onSort?: (sortAttribute: string, direction: ApiSortDirection, query: string | undefined) => void;
  fetching?: boolean;
  responsive?: boolean;
  striped?: boolean;
  className?: string;
  emptyMessage?: string;
  hover?: boolean;
  expandable?: boolean;
  expandedByDefault?: boolean;
  hidePageInfo?: boolean;
  showBorders?: boolean;
}

export const Table = <T,>({
  total,
  page,
  size,
  items,
  header,
  body,
  footer,
  fetchPage,
  query,
  onSearch,
  actions,
  filters,
  title,
  attributes,
  schema,
  onSort,
  fetching = false,
  responsive = true,
  striped = true,
  className = '',
  emptyMessage,
  hover = true,
  expandable = false,
  expandedByDefault = true,
  hidePageInfo = false,
  showBorders = false,
}: TableInterface<T>): React.JSX.Element => {
  const hasSearchOrActionsOrTitle = onSearch || actions || title;
  const [sortAttribute, setSortAttribute] = useState('');
  const [sortDirection, setSortDirection] = useState(ApiSortDirection.ASC);
  const [searchQuery, setSearchQuery] = useState(query);
  const [expanded, setExpanded] = useState(expandedByDefault);

  const onSearchDebounced = useMemo(() => {
    return onSearch && debounce(onSearch, 300);
  }, [onSearch]);

  const handleSort = (attribute: string, direction: ApiSortDirection) => {
    setSortAttribute(attribute);
    setSortDirection(direction);
    onSort && onSort(attribute, direction, searchQuery);
  };

  const renderAttributes = () => {
    return (
      <tr>
        {attributes &&
          attributes.map((a) => (
            <th key={a.key}>
              <div className='attribute-header'>
                {schema
                  ? getDisplayName(
                      schema.schemaFields
                        .filter((schemaField) => !isChildField(schemaField))
                        .find((schemaField) => schemaField.fieldName === a.key)
                    ) || a.key
                  : a.key}
                {a.sortable && (
                  <Sorter
                    attribute={a.key}
                    sortAttribute={sortAttribute}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                )}
              </div>
            </th>
          ))}
      </tr>
    );
  };

  return (
    <React.Fragment>
      <Card body={false} className={className + (showBorders ? ' border-table-card' : '')}>
        {hasSearchOrActionsOrTitle && (
          <Card.Header
            data-testid='card-header'
            className={expandable ? 'cursor-pointer' : ''}
            onClick={(e: any) => {
              if (expandable) {
                e.preventDefault();
                setExpanded(!expanded);
              }
            }}
          >
            {title && <h4 className='card-header-title'>{title}</h4>}
            {filters && onSearch && (
              <Col md={3} onClick={(e: any) => e.stopPropagation()}>
                {filters()}
              </Col>
            )}
            {filters && !onSearch && (
              <Col md={6} onClick={(e: any) => e.stopPropagation()}>
                {filters()}
              </Col>
            )}
            <Row className='align-items-center'>
              <Col>
                {onSearch && (
                  <Row className='align-items-center justify-content-end'>
                    <Col md='auto' className='pe-0'>
                      <span className='fe fe-search text-muted' />
                    </Col>
                    <Col md='auto'>
                      <input
                        type='search'
                        className='form-control form-control-flush search'
                        placeholder='Search'
                        value={searchQuery || ''}
                        onChange={({ target }: ChangeEvent<HTMLInputElement>) => {
                          const targetValue = target.value ? target.value : undefined;
                          setSearchQuery(targetValue);
                          onSearchDebounced && onSearchDebounced(targetValue);
                        }}
                      />
                    </Col>
                  </Row>
                )}
              </Col>
              {actions && <Col md='auto'>{actions()}</Col>}
            </Row>
            {expandable && <ShowHideButton expanded={expanded} />}
          </Card.Header>
        )}
        {fetching ? (
          <LoadingOverlay loading={fetching}>
            <Card.Body>
              <div className='small'>Loading ...</div>
            </Card.Body>
          </LoadingOverlay>
        ) : items.length > 0 ? (
          <Collapse in={(expandable && expanded) || !expandable}>
            <div data-testid='table-body'>
              <ReactTable responsive={responsive} striped={striped} hover={hover} size='sm' className='card-table'>
                <thead>{header ? header(items) : renderAttributes()}</thead>
                <tbody>{body(items)}</tbody>
                {footer && <tfoot>{footer()}</tfoot>}
              </ReactTable>
              {Math.ceil(total / size) > 1 && (
                <Card.Footer>
                  <div className='row align-items-center justify-content-end'>
                    {!hidePageInfo && <PageInfo total={total} page={page} size={size} items={items} />}
                    <div className='col-auto'>
                      <Pagination page={page} size={size} total={total} fetchPage={fetchPage} />
                    </div>
                  </div>
                </Card.Footer>
              )}
            </div>
          </Collapse>
        ) : (
          <>
            <Card.Body>
              <div className='text-s'>{emptyMessage ? emptyMessage : 'Nothing to show here, yet.'}</div>
            </Card.Body>
            {page > 0 && (
              <Card.Footer>
                <div className='row align-items-center justify-content-end'>
                  <div className='col-auto'>
                    <Pagination page={page} size={size} total={total} fetchPage={fetchPage} />
                  </div>
                </div>
              </Card.Footer>
            )}
          </>
        )}
      </Card>
    </React.Fragment>
  );
};

export default Table;
